import { auth } from '../config/firebase';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

async function apiFetch(path, params = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const token = await user.getIdToken();
  const query = new URLSearchParams(params).toString();
  const url = `${API_BASE}${path}${query ? '?' + query : ''}`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }

  return res.json();
}

// ── Dashboard Config ───────────────────────────────────────────────────────

/** Get list of dashboards the current user has access to */
export async function getUserDashboards() {
  return apiFetch('/dashboards');
}

/** Get dashboard config (questions, filters, etc.) for a specific dashboard */
export async function getDashboardConfig(clientKey) {
  return apiFetch(`/dashboards/${clientKey}/config`);
}

// ── Chart Data ─────────────────────────────────────────────────────────────

/** Average allocation data for a question (current wave) */
export async function getAllocationData(clientKey, questionId, filters = {}, valueMappings = {}) {
  return apiFetch(`/dashboards/${clientKey}/allocation`, {
    questionId,
    ...flattenFilters(filters, valueMappings)
  });
}

/** Allocation trendline data */
export async function getAllocationTrend(clientKey, questionId, filters = {}, { trendStartDate, valueMappings = {} } = {}) {
  return apiFetch(`/dashboards/${clientKey}/allocation-trend`, {
    questionId,
    ...flattenFilters(filters, valueMappings),
    ...(trendStartDate ? { trendStartDate } : {})
  });
}

/** Distribution bucket data */
export async function getDistribution(clientKey, questionId, filters = {}, valueMappings = {}) {
  return apiFetch(`/dashboards/${clientKey}/distribution`, {
    questionId,
    ...flattenFilters(filters, valueMappings)
  });
}

/** Commitment/Rejection Index data */
export async function getCohorts(clientKey, questionId, type, filters = {}, valueMappings = {}) {
  return apiFetch(`/dashboards/${clientKey}/cohorts`, {
    questionId,
    type, // 'commitment' or 'rejection'
    ...flattenFilters(filters, valueMappings)
  });
}

/** Commitment/Rejection trendline data */
export async function getCohortsTrend(clientKey, questionId, type, filters = {}, { trendStartDate, valueMappings = {} } = {}) {
  return apiFetch(`/dashboards/${clientKey}/cohorts-trend`, {
    questionId,
    type,
    ...flattenFilters(filters, valueMappings),
    ...(trendStartDate ? { trendStartDate } : {})
  });
}

/** Custom filter values for the filter bar */
export async function getCustomFilters(clientKey) {
  return apiFetch(`/dashboards/${clientKey}/custom-filters`);
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Convert filter state {Party: ["Democrat","Republican"], Age: ["18-29"]}
 *  to flat params: { filter_Party: "Democrat,Republican", filter_Age: "18-29" }
 *
 *  When valueMappings is provided, grouped display values are expanded to their
 *  underlying raw values. E.g. if valueMappings.Race = { "BIPOC": ["African American", "Asian", ...] }
 *  and the user selected "BIPOC", the param becomes filter_Race=African American,Asian,... */
function flattenFilters(filters, valueMappings = {}) {
  const result = {};
  for (const [key, values] of Object.entries(filters)) {
    if (values && values.length > 0) {
      const mappings = valueMappings[key];
      if (mappings) {
        // Expand grouped display values to raw values
        const expanded = [];
        for (const v of values) {
          if (mappings[v]) {
            expanded.push(...mappings[v]);
          } else {
            expanded.push(v);
          }
        }
        result[`filter_${key}`] = expanded.join('|');
      } else {
        result[`filter_${key}`] = values.join('|');
      }
    }
  }
  return result;
}
